/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import theme from '../../../../../Shared/Themes/theme';
import { BoxShadow } from '../../../../../Shared/Constants/Style.Constants';
import { PersonName } from '../AllJobs.Style';
import { buttonStyles } from '../../../../Jobs/Components/UpdateShiftDate/UpdateShiftDate.Style';
import { JobNameContainer } from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { Icons } from '../../../../../Shared/Constants/Icons';
import { Options } from '../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import WhiteTooltip from '../../../../../Shared/Components/Common/CommonStyle/Tooltip.Style';
import CustomDropDownAutoComplete from '../../../../../Shared/Components/Common/CustomDropDown/CustomDropdownAutoComplete';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import ScheduleServices from './ScheduleServices';
import { IAddServiceFlowProps } from './ModifyServices.Model';
import { useModifyServicesContext } from './ModifyServicesContext';

// eslint-disable-next-line sonarjs/cognitive-complexity
const AddServiceFlow: React.FC<IAddServiceFlowProps> = (props) => {
  const {
    service,
    setService,
    serviceTasks,
    setServiceTasks,
    selectedJobs,
    servicesData,
    serviceName,
    setServiceName,
    serviceDescription,
    setServiceDescription,
    createService,
    setCreateService,
    serviceConfirmed,
    setServiceConfirmed,
    createdService,
    isCreateServiceLoading,
    handleConfirmService,
    handleCancelService,
    createNewTask,
    setCreateNewTask,
    isTasksOpen,
    setIsTasksOpen,
    taskConfirmed,
    setTaskConfirmed,
    createdTask,
    selectedTaskType,
    handleTaskTypeChange,
    taskOptions,
    handleTaskChanges,
    totalCount,
    handlePaginationChange,
    taskName,
    setTaskName,
    taskDescription,
    setTaskDescription,
    isCreateTaskLoading,
    handleCancelTask,
    handleConfirmTask,
    isSubTasksOpen,
    setSubTasksOpen,
    taskSubTasks,
    setSubTaskConfirmed,
    createSubTask,
    setCreateSubTask,
    selectedSubTaskType,
    setSelectedSubTaskType,
    handleSubTaskTypeChange,
    createdSubTask,
    handleSubTaskChanges,
    subTaskCreationType,
    handleSubTaskCreationType,
    subTaskName,
    setSubTaskName,
    subTaskDescription,
    setSubTaskDescription,
    isCreateSubTaskLoading,
    handleConfirmSubTask,
    subTaskConfirmed,
    setIsAddService,
    resetAllStates,
    setAddServiceToSchedule,
    setSelectedTaskObj,
    resetTasksAndSubTasks,
    resetSubTasks,
  } = props;

  const {
    selectedTask,
    setSelectedTask,
    selectedSubTask,
    setSelectedSubTask,
    searchString,
    setSearchString,
    searchSubTaskString,
    setSearchSubTaskString,
    currentPage,
    isTasksLoading,
  } = useModifyServicesContext();

  const { radioButton, radioButtonText, textField, headingText, divider } = buttonStyles;
  const { t } = useTranslation(['taskManagement', 'altTexts']);

  return (
    <Box
      sx={{
        borderRadius: '1rem',
        backgroundColor: theme.palette.common.white,
        boxShadow: BoxShadow.BoxShadow1,
        padding: '1.5rem',
        margin: '1.875rem 0 1.5rem 0',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '1.5rem',
      }}>
      <PersonName>{t('taskManagement:AddServices')}:&nbsp;</PersonName>
      <Divider sx={{ ...divider, margin: '1.5rem 0' }} />
      <Typography sx={{ ...headingText, marginTop: '2rem' }}>{t('taskManagement:Services')}</Typography>

      <ScheduleServices
        service={service}
        setService={setService}
        serviceTasks={serviceTasks}
        setServiceTasks={setServiceTasks}
        customerId={selectedJobs?.[0]?.customerId}
        existingServiceId={servicesData?.[0]?.mdServiceId}
        setCreateNewTask={setCreateNewTask}
        setServiceName={setServiceName}
        serviceName={serviceName}
        setServiceDescription={setServiceDescription}
        serviceDescription={serviceDescription}
        createService={createService}
        setCreateService={setCreateService}
        setServiceConfirmed={setServiceConfirmed}
        setIsTasksOpen={setIsTasksOpen}
        isTasksOpen={isTasksOpen}
        createNewTask={createNewTask}
        serviceConfirmed={serviceConfirmed}
        setTaskConfirmed={setTaskConfirmed}
        createdService={createdService}
        setSelectedTaskObj={setSelectedTaskObj}
        resetTasksAndSubTasks={resetTasksAndSubTasks}
        resetSubTasks={resetSubTasks}
      />

      {createService && !serviceConfirmed && !createdService && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 2 }}>
          <Button type={ButtonType.Secondary} label={t('taskManagement:Cancel')} onClick={handleCancelService} />
          <Button
            type={ButtonType.Primary}
            label={t('taskManagement:ConfirmService')}
            onClick={handleConfirmService}
            disabled={!(service?.[0]?.mdServiceId || (createService && serviceName && serviceDescription))}
            isLoading={isCreateServiceLoading}
          />
        </Box>
      )}

      {serviceConfirmed && createNewTask && (
        <>
          {createdService && (
            <Typography sx={{ ...headingText, marginTop: '2rem' }}>{t('taskManagement:Tasks')}</Typography>
          )}
          {!createdTask && (
            <FormControl component="fieldset">
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.h3?.fontSize,
                    fontWeight: theme.typography.fontWeightBold,
                  }}>
                  {
                    <span>
                      {t('taskManagement:ChooseTaskSource')}
                      <span className="mandatory-field"> *</span>
                    </span>
                  }
                </Typography>
              </Box>
              <RadioGroup value={selectedTaskType} onChange={handleTaskTypeChange}>
                <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
                  <FormControlLabel
                    value="select-from-system"
                    control={
                      <Radio
                        sx={{
                          ...radioButton,
                        }}
                      />
                    }
                    label={t('taskManagement:SelectFromSystem')}
                    sx={{
                      ...radioButtonText,
                    }}
                  />
                </Box>
                <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
                  <FormControlLabel
                    value="create-task"
                    control={
                      <Radio
                        sx={{
                          ...radioButton,
                        }}
                      />
                    }
                    label={t('taskManagement:CreateNewTask')}
                    sx={{
                      ...radioButtonText,
                    }}
                  />
                </Box>
              </RadioGroup>
            </FormControl>
          )}
          {createNewTask && selectedTaskType === 'select-from-system' && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', marginTop: '2rem' }}>
              <CustomDropDownAutoComplete
                title={t('taskManagement:Tasks')}
                options={taskOptions}
                selectedValue={selectedTask}
                onChange={handleTaskChanges}
                isLoading={isTasksLoading}
                totalCounts={totalCount}
                handlePaginationChanges={handlePaginationChange}
                currentPages={currentPage}
                searchString={searchString}
                setSearchString={setSearchString}
                dropDownHeight={500}
                isInline={true}
              />
              <JobNameContainer
                sx={{ marginBottom: '0rem' }}
                onClick={() => {
                  setCreateNewTask(false);
                  setTaskConfirmed(false);
                  setSelectedTask('');
                  setSearchString('');
                }}>
                <WhiteTooltip title={'Cancel'}>
                  <img src={Icons.CancelIcon} alt="Cancel" width={24} height={24} />
                </WhiteTooltip>
              </JobNameContainer>
            </Box>
          )}
          {createNewTask && selectedTaskType === 'create-task' && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, marginTop: '2rem' }}>
                <JobNameContainer>
                  <TextField
                    sx={textField}
                    id="outlined-required"
                    label={
                      <span>
                        {t('taskManagement:TaskName')}
                        <span className="mandatory-field"> *</span>
                      </span>
                    }
                    value={taskName}
                    onChange={(event) => {
                      const value = event?.target?.value;
                      setTaskName(value);
                    }}
                    disabled={createdTask}
                  />
                </JobNameContainer>
                <JobNameContainer>
                  <TextField
                    sx={textField}
                    id="outlined-required"
                    label={
                      <span>
                        {t('taskManagement:TaskDescription')}
                        <span className="mandatory-field"> *</span>
                      </span>
                    }
                    value={taskDescription}
                    onChange={(event) => {
                      const value = event?.target?.value;
                      setTaskDescription(value);
                    }}
                    disabled={createdTask}
                  />
                </JobNameContainer>
                {!createdTask && (
                  <JobNameContainer
                    onClick={() => {
                      setCreateNewTask(false);
                      setTaskConfirmed(false);
                      setTaskName('');
                      setTaskDescription('');
                    }}>
                    <WhiteTooltip title={'Cancel'}>
                      <img src={Icons.CancelIcon} alt="Cancel" width={24} height={24} />
                    </WhiteTooltip>
                  </JobNameContainer>
                )}
              </Box>
              {!createdTask && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 2 }}>
                  <Button type={ButtonType.Secondary} label={t('taskManagement:Cancel')} onClick={handleCancelTask} />
                  <Button
                    type={ButtonType.Primary}
                    label={t('taskManagement:ConfirmTask')}
                    onClick={handleConfirmTask}
                    disabled={!taskName || !taskDescription}
                    isLoading={isCreateTaskLoading}
                  />
                </Box>
              )}
            </>
          )}
          {taskConfirmed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '2rem', color: '#006B6B' }}>
              <img src={Icons.CheckedIcon} width={20} height={20} alt="confirmed" />
              <Typography sx={{ fontWeight: 600 }}>{t('taskManagement:TaskConfirmed')}</Typography>
            </Box>
          )}
        </>
      )}

      {taskConfirmed && (
        <>
          <Divider sx={{ ...divider, margin: '1.5rem 0' }} />

          <Typography sx={{ ...headingText, marginTop: '2rem' }}>{t('taskManagement:SubTasks')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', marginBottom: '2.5rem' }}>
            <TextField
              select
              sx={{
                ...textField,
                opacity: createSubTask ? '0.5' : '1',
                cursor: createSubTask ? 'not-allowed' : 'pointer',
              }}
              disabled={createSubTask}
              label={<span>{t('taskManagement:SubTasks')}</span>}
              value={selectedSubTask}
              onChange={(event) => {
                setSelectedSubTask(event?.target?.value);
                setSubTaskConfirmed(true);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setSubTasksOpen(!isSubTasksOpen)}
                      style={{
                        padding: 0,
                        opacity: createSubTask ? '0.5' : '1',
                        cursor: createSubTask ? 'not-allowed' : 'pointer',
                      }}
                      disabled={createSubTask}>
                      <img
                        src={isSubTasksOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                        alt={isSubTasksOpen ? 'down' : 'up'}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              SelectProps={{
                open: isSubTasksOpen,
                IconComponent: () => null,
                onOpen: () => setSubTasksOpen(true),
                onClose: () => setSubTasksOpen(false),
                MenuProps: {
                  PaperProps: {
                    style: {
                      boxShadow: BoxShadow.BoxShadow,
                    },
                  },
                },
              }}>
              {taskSubTasks && taskSubTasks?.length > 0 ? (
                taskSubTasks?.map((schedule: any, index: number) => (
                  <Options key={index} value={schedule?.mdTaskId}>
                    {schedule?.taskName}
                  </Options>
                ))
              ) : (
                <Options disabled>{t('taskManagement:NoSubTasksFound')}</Options>
              )}
            </TextField>
            <Box
              sx={{
                color: '#006B6B',
                letterSpacing: '0.25px',
                fontSize: theme.typography.h5?.fontSize,
                fontWeight: theme.typography.fontWeightBold,
                fontFamily: theme.typography.fontFamily,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
                opacity: createSubTask ? '0.5' : '1',
                cursor: createSubTask ? 'not-allowed' : 'pointer',
              }}
              onClick={() => {
                setSearchString('');
                setCreateSubTask(true);
                setSelectedSubTask('');
                setSubTaskConfirmed(false);
              }}>
              <img src={Icons.AddIcon} width={24} height={24} alt={t('altTexts:add')} />
              {t('taskManagement:AddNewSubTask')}
            </Box>
          </Box>
          {createSubTask && (
            <FormControl component="fieldset">
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.h3?.fontSize,
                  fontWeight: theme.typography.fontWeightBold,
                }}>
                {
                  <span>
                    {t('taskManagement:ChooseSubTaskSource')}
                    <span className="mandatory-field"> *</span>
                  </span>
                }
              </Typography>
              <RadioGroup value={selectedSubTaskType} onChange={handleSubTaskTypeChange}>
                <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
                  <FormControlLabel
                    value="select-from-system"
                    control={
                      <Radio
                        sx={{
                          ...radioButton,
                        }}
                      />
                    }
                    label={t('taskManagement:SelectFromSystem')}
                    sx={{
                      ...radioButtonText,
                    }}
                    disabled={createdSubTask}
                  />
                </Box>
                <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
                  <FormControlLabel
                    value="create-sub-task"
                    control={
                      <Radio
                        sx={{
                          ...radioButton,
                        }}
                      />
                    }
                    label={t('taskManagement:CreateNewSubTask')}
                    sx={{
                      ...radioButtonText,
                    }}
                    disabled={createdSubTask}
                  />
                </Box>
              </RadioGroup>
            </FormControl>
          )}

          {createSubTask && selectedSubTaskType === 'select-from-system' && (
            <>
              <Box sx={{ marginTop: '2rem', display: 'flex', flexDirection: 'row', gap: 2 }}>
                <CustomDropDownAutoComplete
                  title={t('taskManagement:SubTasks')}
                  options={taskOptions}
                  selectedValue={selectedSubTask}
                  onChange={handleSubTaskChanges}
                  isLoading={isTasksLoading}
                  totalCounts={totalCount}
                  handlePaginationChanges={handlePaginationChange}
                  currentPages={currentPage}
                  searchString={searchSubTaskString}
                  setSearchString={setSearchSubTaskString}
                  dropDownHeight={500}
                  isInline={true}
                />
                <JobNameContainer
                  sx={{ marginBottom: '0rem' }}
                  onClick={() => {
                    setCreateSubTask(false);
                    setSelectedSubTaskType('');
                    setSubTaskConfirmed(false);
                  }}>
                  <WhiteTooltip title={'Cancel'}>
                    <img src={Icons.CancelIcon} alt="Cancel" width={24} height={24} />
                  </WhiteTooltip>
                </JobNameContainer>
              </Box>
              {!createdSubTask && (
                <Box sx={{ marginTop: '2rem' }}>
                  <FormControl component="fieldset">
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: theme.typography.h3?.fontSize,
                        fontWeight: theme.typography.fontWeightBold,
                      }}>
                      {
                        <span>
                          {t('taskManagement:DoYouWantToAddSubTaskPermanentlyOrSchedule')}
                          <span className="mandatory-field"> *</span>
                        </span>
                      }
                    </Typography>
                    <RadioGroup value={subTaskCreationType} onChange={handleSubTaskCreationType}>
                      <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
                        <FormControlLabel
                          value="permanent"
                          control={
                            <Radio
                              sx={{
                                ...radioButton,
                              }}
                            />
                          }
                          label={t('taskManagement:Permanently')}
                          sx={{
                            ...radioButtonText,
                          }}
                        />
                      </Box>
                      <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
                        <FormControlLabel
                          value="schedule"
                          control={
                            <Radio
                              sx={{
                                ...radioButton,
                              }}
                            />
                          }
                          label={t('taskManagement:Schedule')}
                          sx={{
                            ...radioButtonText,
                          }}
                        />
                      </Box>
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            </>
          )}
          {createSubTask && selectedSubTaskType === 'create-sub-task' && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, marginTop: '2rem' }}>
                <JobNameContainer>
                  <TextField
                    sx={textField}
                    id="outlined-required"
                    label={
                      <span>
                        {t('taskManagement:SubTaskName')}
                        <span className="mandatory-field"> *</span>
                      </span>
                    }
                    value={subTaskName}
                    onChange={(event) => {
                      const value = event?.target?.value;
                      setSubTaskName(value);
                    }}
                    disabled={createdSubTask}
                  />
                </JobNameContainer>
                <JobNameContainer>
                  <TextField
                    sx={textField}
                    id="outlined-required"
                    label={
                      <span>
                        {t('taskManagement:SubTaskDescription')}
                        <span className="mandatory-field"> *</span>
                      </span>
                    }
                    value={subTaskDescription}
                    onChange={(event) => {
                      const value = event?.target?.value;
                      setSubTaskDescription(value);
                    }}
                    disabled={createdSubTask}
                  />
                </JobNameContainer>

                {!createdSubTask && (
                  <JobNameContainer
                    onClick={() => {
                      setCreateSubTask(false);
                      setSelectedSubTaskType('');
                      setSubTaskConfirmed(false);
                    }}>
                    <WhiteTooltip title={'Cancel'}>
                      <img src={Icons.CancelIcon} alt="Cancel" width={24} height={24} />
                    </WhiteTooltip>
                  </JobNameContainer>
                )}
              </Box>
              {!createdSubTask && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 2 }}>
                  <Button type={ButtonType.Secondary} label={t('taskManagement:Cancel')} onClick={handleCancelTask} />
                  <Button
                    type={ButtonType.Primary}
                    label={t('taskManagement:ConfirmSubTask')}
                    onClick={handleConfirmSubTask}
                    disabled={!subTaskName || !subTaskDescription}
                    isLoading={isCreateSubTaskLoading}
                  />
                </Box>
              )}
              {subTaskConfirmed && (
                <Box sx={{ marginTop: '2rem' }}>
                  <FormControl component="fieldset">
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: theme.typography.h3?.fontSize,
                        fontWeight: theme.typography.fontWeightBold,
                      }}>
                      {
                        <span>
                          {t('taskManagement:DoYouWantToAddSubTaskPermanentlyOrSchedule')}
                          <span className="mandatory-field"> *</span>
                        </span>
                      }
                    </Typography>
                    <RadioGroup value={subTaskCreationType} onChange={handleSubTaskCreationType}>
                      <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
                        <FormControlLabel
                          value="permanent"
                          control={
                            <Radio
                              sx={{
                                ...radioButton,
                              }}
                            />
                          }
                          label={t('taskManagement:Permanently')}
                          sx={{
                            ...radioButtonText,
                          }}
                        />
                      </Box>
                      <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
                        <FormControlLabel
                          value="schedule"
                          control={
                            <Radio
                              sx={{
                                ...radioButton,
                              }}
                            />
                          }
                          label={t('taskManagement:Schedule')}
                          sx={{
                            ...radioButtonText,
                          }}
                        />
                      </Box>
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            </>
          )}
          {subTaskConfirmed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '2rem', color: '#006B6B' }}>
              <img src={Icons.CheckedIcon} width={20} height={20} alt="confirmed" />
              <Typography sx={{ fontWeight: 600 }}>{t('taskManagement:SubTaskConfirmed')}</Typography>
            </Box>
          )}
        </>
      )}
      {serviceConfirmed && taskConfirmed && subTaskConfirmed && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
          <Button
            type={ButtonType.Secondary}
            label={t('taskManagement:Cancel')}
            onClick={() => {
              setIsAddService(false);
              resetAllStates();
              setAddServiceToSchedule(false);
            }}
          />
          <Button
            type={ButtonType.Primary}
            label={t('taskManagement:Confirm')}
            onClick={() => {
              setAddServiceToSchedule(true);
            }}
            disabled={!subTaskCreationType}
          />
        </Box>
      )}
      {serviceConfirmed && taskConfirmed && !selectedSubTask && !createSubTask && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
          <Button
            type={ButtonType.Secondary}
            label={t('taskManagement:Cancel')}
            onClick={() => {
              setIsAddService(false);
              resetAllStates();
              setAddServiceToSchedule(false);
            }}
          />
          <Button
            type={ButtonType.Primary}
            label={t('taskManagement:Confirm')}
            onClick={() => {
              setAddServiceToSchedule(true);
            }}
          />
        </Box>
      )}
      {!(serviceConfirmed && taskConfirmed && (subTaskConfirmed || (!selectedSubTask && !createSubTask))) &&
        !(createService && !serviceConfirmed && !createdService) &&
        !(selectedTaskType === 'create-task' && !createdTask) &&
        !(selectedSubTaskType === 'create-sub-task' && !createdSubTask) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
            <Button
              type={ButtonType.Secondary}
              label={t('taskManagement:Back')}
              onClick={() => {
                setIsAddService(false);
                resetAllStates();
                setAddServiceToSchedule(false);
              }}
            />
          </Box>
        )}
    </Box>
  );
};

export default AddServiceFlow;
